import { toast } from "$global-providers/toast-provider";
import type { InvoiceCurrencyExchange } from "$routes/_lib/billing-v2/currency-exchange";
import { gql } from "@apollo/client";
import type {
  Amount,
  InvoicePersonName,
  PaymentMethod,
  TransactionType,
} from "elephant-web/gql";
import { useEffect, useState } from "react";
import { defineMessage, useIntl } from "react-intl";
import { useInvoiceQuery } from "./__generated__/usePaymentHistory.gql";

gql`
  query invoice($invoiceId: String!) {
    invoice(invoiceId: $invoiceId) {
      transactions {
        _id
        actionedBy {
          person {
            firstname
            middlename
            lastname
          }
        }
        type
        timestamp
        amount {
          value
          currency
        }
        payment {
          paymentMethod
          authCode
        }
        currencyExchange {
          from {
            currency
            value
          }
          to {
            currency
            value
          }
          exchangeRate {
            id
            baseCurrency
            quoteCurrency
            rate
            effectiveDate
          }
        }
      }
    }
  }
`;

const ERROR_MESSAGE = defineMessage({
  id: "invoicing.paymentHistory.error.msg",
  defaultMessage: "Failed to get the payment history for this invoice",
});

export function usePaymentHistory(invoiceId: string) {
  const [paymentTransactions, setPaymentTransactions] = useState<Array<{
    id?: string | null;
    cashier?: InvoicePersonName | null;
    date?: string | null;
    amount?: Amount | null;
    transactionType?: TransactionType | null;
    paymentMethod?: PaymentMethod | null;
    transactionCode?: string | null;
    currencyExchange?: InvoiceCurrencyExchange | null;
  }> | null>(null);
  const { formatMessage } = useIntl();

  const { loading, error, data } = useInvoiceQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      invoiceId,
    },
    onError() {
      toast.error(formatMessage(ERROR_MESSAGE), {});
    },
  });

  useEffect(() => {
    if (loading || error || !data?.invoice) {
      return;
    }
    const {
      invoice: { transactions },
    } = data;
    const formattedTransactions = transactions.map((t) => {
      return {
        id: t._id,
        cashier: t.actionedBy?.person,
        date: t.timestamp,
        amount: t.amount,
        transactionType: t.type,
        paymentMethod: t.payment?.paymentMethod,
        transactionCode: t.payment?.authCode,
        currencyExchange: t.currencyExchange,
      };
    });
    setPaymentTransactions(formattedTransactions);
  }, [loading, error, data]);

  return { loading, error, paymentTransactions };
}
